import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "../dataTable/DataTableColumnHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  canDeleteOrgLibraryItem,
  canEditOrgLibraryItem,
} from "@/lib/libraryAccess";

export type Metric = {
  id: string;
  name: string;
  unit: string | null;
  org_id: string;
  created_by?: string | null;
  os_integration_type?: string | null;
  daily_target?: number | null;
};

type ColumnsOptions = {
  hideEditButton?: boolean;
  weightUnitLabel?: string;
  userId?: string | null;
  canManageLibrary?: boolean;
};

export const WEIGHT_METRIC_ID = "11d92087-b60f-4c21-b685-1021662da260";

const getColumnsOptions = (
  hideEditButtonOrOptions: boolean | ColumnsOptions,
): ColumnsOptions => {
  if (
    typeof hideEditButtonOrOptions === "object" &&
    hideEditButtonOrOptions !== null &&
    ("hideEditButton" in hideEditButtonOrOptions ||
      "weightUnitLabel" in hideEditButtonOrOptions ||
      "userId" in hideEditButtonOrOptions ||
      "canManageLibrary" in hideEditButtonOrOptions)
  ) {
    return hideEditButtonOrOptions;
  }

  return { hideEditButton: Boolean(hideEditButtonOrOptions) };
};

const getMetricUnitLabel = (metric: Metric, weightUnitLabel?: string) => {
  if (metric.id === WEIGHT_METRIC_ID) {
    return weightUnitLabel ?? metric.unit ?? "";
  }

  return metric.unit ?? "";
};

export const columns = (
  hideEditButtonOrOptions: boolean | ColumnsOptions,
): ColumnDef<Metric>[] => {
  const {
    hideEditButton = false,
    weightUnitLabel,
    userId,
    canManageLibrary = false,
  } = getColumnsOptions(hideEditButtonOrOptions);

  return [
    {
      id: "name",
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
    },
    {
      id: "unit",
      accessorFn: (row) => getMetricUnitLabel(row, weightUnitLabel),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Unit" />
      ),
      cell: ({ row }) => getMetricUnitLabel(row.original, weightUnitLabel),
    },
    {
      id: "Actions",
      cell: ({ row, onRowSelected, onRowDeleted }) => {
        const metric = row.original;
        const canEdit = canEditOrgLibraryItem(metric, {
          userId,
          canManageLibrary,
        });
        const canDelete = canDeleteOrgLibraryItem(metric, {
          userId,
          canManageLibrary,
        });

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRowSelected?.(metric)}>
                {canEdit ? "Edit" : "View"}
              </DropdownMenuItem>
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.stopPropagation();
                      onRowDeleted?.(metric);
                    }}
                    variant="destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      meta: {
        alwaysHide: hideEditButton,
      },
    },
  ];
};
